import { useMutation } from '@tanstack/react-query';
import { ValidationError } from 'yup';
import {
  invalidateGiftCardItem,
  updateGiftCardSettings,
} from '../../services/gift-cards';
import {
  GiftCardSettings,
  GiftCardSettingsPayload,
  giftCardSettingsSchema,
} from '../../types/gift-cards';
import useUser from '../global/useUser';

interface Options {
  onSuccess?: (result: GiftCardSettings | null) => void | Promise<void>;
  onError?: (error: string) => void | Promise<void>;
  onMutate?: () => void | Promise<void>;
  onSettled?: () => void | Promise<void>;
}

function useUpdateGiftCardSettings({
  onSuccess,
  onError,
  onMutate,
  onSettled,
}: Options = {}) {
  const { user } = useUser();

  return useMutation({
    mutationFn: async (payload: GiftCardSettingsPayload) => {
      if (!user?.business?.id) {
        throw new Error('No business ID found');
      }

      const body = await giftCardSettingsSchema.noUnknown().validate(payload, {
        stripUnknown: true,
      });

      const result = await updateGiftCardSettings(user.business.id, body);

      return result;
    },
    onSuccess: async result => {
      if (!result) return;

      await invalidateGiftCardItem();

      await onSuccess?.(result);
    },
    onError: (error: Error) => {
      if (error instanceof ValidationError) {
        onError?.(error.message);
        return;
      }
      onError?.('Failed to update gift card settings');
    },
    onMutate,
    onSettled,
  });
}

export default useUpdateGiftCardSettings;
